/**
 * Entry point da aplicação, referenciado por `index.html` na raiz do projeto
 * (o Vite trata o HTML como entry, não como template).
 */
// `react-app-polyfill/ie11` foi removido junto com o IE11 do browserslist.
// O `/stable` (core-js) fica por enquanto: tirar os dois de uma vez somaria
// uma variável de risco em Safari/Android antigos no meio da migração.
import "react-app-polyfill/stable";
import React from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import axios from "axios";
import * as _redux from "./redux";
import store, { persistor } from "./redux/store";
import App from "./app/App";
import { hydrateNumber } from "./app/helpers/HydrateNumber";
import { sentryBeforeSend } from "./app/utils/sentryBeforeSend";
// Estes dois vinham de @import dentro do index.scss. O Sass repassa
// @import de .css como @import url() nativo em vez de inlinar, então
// eles passam a ser importados aqui, antes do index.scss.
import "highlight.js/styles/googlecode.css";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import "./index.scss"; // Standard version
// import "./sass/style.react.rtl.css"; // RTL version
import "./_metronic/_assets/plugins/keenthemes-icons/font/ki.css";
import "socicon/css/socicon.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./_metronic/_assets/plugins/flaticon/flaticon.css";
import "./_metronic/_assets/plugins/flaticon2/flaticon.css";
// Datepicker
import "react-datepicker/dist/react-datepicker.css";

import {
  MetronicLayoutProvider,
  MetronicSplashScreenProvider,
  MetronicSubheaderProvider,
} from "./_metronic/layout";
import { MetronicI18nProvider } from "./_metronic/i18n";

/**
 * Leituras diretas, e não destructuring de `process.env`: o `define` do
 * vite.config.ts substitui apenas acessos estáticos por chave. Desestruturar
 * o objeto inteiro deixaria `process` sobrando no bundle.
 */
const PUBLIC_URL = process.env.PUBLIC_URL;
const REACT_APP_SENTRY_ENV = process.env.REACT_APP_SENTRY_ENV;
const REACT_APP_SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const REACT_APP_SENTRY_SAMPLE_RATE = process.env.REACT_APP_SENTRY_SAMPLE_RATE;
const REACT_APP_SENTRY_ENABLED = process.env.REACT_APP_SENTRY_ENABLED;
const REACT_APP_SENTRY_SAMPLE_TRACE_RATE =
  process.env.REACT_APP_SENTRY_SAMPLE_TRACE_RATE;
/**
 * Inject metronic interceptors for axios.
 *
 * @see https://github.com/axios/axios#interceptors
 */
/**
 * Inject metronic interceptors for axios.
 *
 * @see https://github.com/axios/axios#interceptors
 */

/**
 * Creates `axios-mock-adapter` instance for provided `axios` instance, add
 * basic Metronic mocks and returns it.
 *
 * @see https://github.com/ctimmerm/axios-mock-adapter
 */
/* const mock = */

_redux.setupAxios(axios, store);

/**
 * MODO DEMO (dev-only, 2026-09-19) — sessão fake + mock global do back, para abrir a JusCalc
 * Completa no navegador sem login e sem API (`?demo=1`). Tem de rodar ANTES do `PersistGate`
 * (a sessão precisa estar no localStorage quando o redux-persist reidratar) e antes da 1ª
 * requisição do Layout — por isso roda aqui, no topo do entry, e não num `useEffect`.
 *
 * `await import()` e não `require`: o Vite serve ESM e o navegador não tem `require` (o dev
 * server morria no boot). O await de topo segura o resto deste módulo, inclusive o
 * `ReactDOM.render`, até o boot terminar — a mesma garantia que o `require` síncrono dava.
 *
 * As duas constantes são substituídas no build (`define` do vite.config.ts): em produção a
 * condição vira `false` e o esbuild remove o ramo INTEIRO, inclusive o módulo (gate F4).
 */
if (
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_E2E === "1"
) {
  const { bootDemo } = await import(
    "./app/pages/AtualizacaoCompleto/dev/demo/boot"
  );
  const demo = bootDemo();
  // Mock do checkout (`?mockcheckout=<cenário>`). Um adapter só por axios: com o demo
  // ligado, o mock do checkout não instala.
  if (demo === "inativo") {
    const { bootCheckoutMock } = await import(
      "./app/pages/Cadastro/dev/mockCheckout/boot"
    );
    bootCheckoutMock();
  }
}

const isEnabled = value => {
  return value === "true" || value === "1" || value === "yes";
};

if (isEnabled(REACT_APP_SENTRY_ENABLED)) {
  Sentry.init({
    dsn: REACT_APP_SENTRY_DSN,
    integrations: [new BrowserTracing()],
    sampleRate: hydrateNumber(REACT_APP_SENTRY_SAMPLE_RATE, 0.1),
    tracesSampleRate: hydrateNumber(REACT_APP_SENTRY_SAMPLE_TRACE_RATE, 0.1),
    environment: REACT_APP_SENTRY_ENV,
    // Definido no build pelo vite.config.ts; o sourcemap do Sentry é
    // associado ao evento por ele. Fora do build (dev, Jest) não existe.
    // eslint-disable-next-line no-undef
    release: typeof __SENTRY_RELEASE__ === "string" ? __SENTRY_RELEASE__ : undefined,
    normalizeDepth: 10,
    beforeSend: sentryBeforeSend,
  });
}

window.addEventListener('unhandledrejection', event => {
  if (!isEnabled(REACT_APP_SENTRY_ENABLED)) {
    return;
  }

  event.preventDefault();
  
  if (event.reason === 'Timeout' || typeof event.reason === 'string') {
    Sentry.captureException(new Error('UnhandledPromiseRejection_Timeout'), {
      tags: { error_type: 'unhandled_rejection' },
      extra: {
        reason: event.reason,
        originalError: event.reason,
      }
    });

    return;
  }

  Sentry.captureException(event.reason);
});

ReactDOM.render(
  <MetronicI18nProvider>
    <MetronicLayoutProvider>
      <MetronicSubheaderProvider>
        <MetronicSplashScreenProvider>
          <App store={store} persistor={persistor} basename={PUBLIC_URL} />
        </MetronicSplashScreenProvider>
      </MetronicSubheaderProvider>
    </MetronicLayoutProvider>
  </MetronicI18nProvider>,
  document.getElementById("root")
);
